"use client";

import { cn } from "@/lib/utils";
import Link from "next/link";
import Image from "next/image";
import ImagePlaceholder from "./image-placeholder";
import { ArrowUpRight } from "lucide-react";

type CategoryCardProps = {
  readonly name: string;
  readonly category: string;
  readonly imageUrl?: string;
  readonly imageSize?: "default" | "large";
  readonly description?: string;
};

export default function CategoryCard({
  name,
  category,
  imageUrl,
  imageSize = "default",
  description,
}: CategoryCardProps) {
  const isLarge = imageSize === "large";

  return (
    <Link href={`/categories/${category}`} className="block h-full group">
      <article
        className={cn(
          "relative h-full rounded-2xl overflow-hidden",
          "bg-card border border-border/50",
          "transition-all duration-500 ease-out",
          "hover:shadow-xl hover:shadow-primary/5",
          "hover:-translate-y-1"
        )}
      >
        {/* Image Container */}
        <div
          className={cn(
            "relative w-full overflow-hidden bg-white",
            isLarge ? "h-56" : "h-32"
          )}
        >
          {imageUrl ? (
            <>
              <Image
                unoptimized
                src={imageUrl.startsWith("/") ? imageUrl : `/images/product-images/${imageUrl}`}
                alt={name}
                fill
                className="object-contain p-4 transition-transform duration-700 ease-out group-hover:scale-110"
              />
              {/* Gradient overlay on hover */}
              <div className="absolute inset-0 bg-linear-to-t from-black/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </>
          ) : (
            <div className="h-full flex justify-center items-center bg-accent/50">
              <ImagePlaceholder />
            </div>
          )}

          {/* Corner accent */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-linear-to-bl from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </div>

        {/* Content */}
        <div className="p-5 space-y-3">
          {/* Title row */}
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-semibold text-lg leading-tight text-foreground group-hover:text-primary transition-colors duration-300">
              {name.toUpperCase()}
            </h3>
            {/* Arrow indicator */}
            <span className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center transition-all duration-300 group-hover:bg-primary group-hover:scale-110">
              <ArrowUpRight className="w-4 h-4 text-primary transition-all duration-300 group-hover:text-primary-foreground group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>

          {/* Description */}
          {description && (
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
              {description}
            </p>
          )}

        </div>

        {/* Bottom accent line - anchored to bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-linear-to-r from-primary to-secondary scale-x-0 origin-left transition-transform duration-500 ease-out group-hover:scale-x-100" />
      </article>
    </Link>
  );
}
